import { Controller } from "@hotwired/stimulus"
import { template } from "lodash"

// kontroler strony rozliczenia klienta biura — checkboxy dokumentów, live preview, pozycje jednorazowe
export default class extends Controller {
  static targets = [
    "costsCheckbox", "rejectedCheckbox",
    "salesCount",
    "totalDocs", "tierChip",
    "chipEmptyTemplate", "chipMatchedTemplate", "chipIndividualTemplate", "chipOverflowTemplate",
    "mainLineName", "mainLineDesc", "mainLinePrice",
    "overflowRow", "overflowName", "overflowDesc", "overflowPrice",
    "extraRow",
    "onetimeContainer", "onetimeTemplate", "onetimeRow",
    "grandTotal", "submitButton"
  ]

  static values = {
    preview: Object,
    docUnit: String,
    currencySymbol: String,
    vatExempt: String,
    tierUpTo: String,
    docsWord: String,
    overflowDocsLabel: String,
  }

  connect() {
    this.chipEmptyTpl = template(this.chipEmptyTemplateTarget.innerHTML)
    this.chipMatchedTpl = template(this.chipMatchedTemplateTarget.innerHTML)
    this.chipIndividualTpl = template(this.chipIndividualTemplateTarget.innerHTML)
    this.chipOverflowTpl = template(this.chipOverflowTemplateTarget.innerHTML)
    this.recalculate()
  }

  recalculate() {
    const data = this.previewValue
    const includeCosts = this.hasCostsCheckboxTarget && this.costsCheckboxTarget.checked
    const includeRejected = this.hasRejectedCheckboxTarget && this.rejectedCheckboxTarget.checked

    // suma dokumentów
    let totalDocs = data.sales_count
    if (includeCosts) totalDocs += data.costs_count
    if (includeRejected) totalDocs += data.rejected_count

    if (this.hasTotalDocsTarget) this.totalDocsTarget.textContent = `${totalDocs} ${this.docUnitValue}`

    // dopasowanie przedziału
    const tiers = data.tiers || []
    const matched = tiers.find(t => totalDocs >= t.from && totalDocs <= t.to)
    let mainPrice = 0
    let overflowPrice = 0
    let overflowDocs = 0
    let isIndividual = false

    if (matched) {
      mainPrice = matched.price
    } else if (tiers.length > 0 && totalDocs > 0) {
      const lastTier = tiers[tiers.length - 1]
      if (totalDocs > lastTier.to) {
        mainPrice = lastTier.price
        overflowDocs = totalDocs - lastTier.to

        if (data.overflow_type === "fixed") {
          overflowPrice = data.overflow_price
        } else if (data.overflow_type === "per_doc") {
          overflowPrice = overflowDocs * data.overflow_price
        } else if (data.overflow_type === "individual") {
          mainPrice = 0
          isIndividual = true
        }
      }
    }

    // chip z przedziałem — HTML z lodash template
    if (this.hasTierChipTarget) {
      if (totalDocs === 0) {
        this.tierChipTarget.innerHTML = this.chipEmptyTpl({})
      } else if (isIndividual) {
        this.tierChipTarget.innerHTML = this.chipIndividualTpl({})
      } else if (matched) {
        this.tierChipTarget.innerHTML = this.chipMatchedTpl({
          FROM: matched.from,
          TO: matched.to,
          PRICE: this._formatCurrency(matched.price),
        })
      } else if (tiers.length > 0) {
        const lastTier = tiers[tiers.length - 1]
        this.tierChipTarget.innerHTML = this.chipOverflowTpl({
          TO: lastTier.to,
          PRICE: this._formatCurrency(mainPrice + overflowPrice),
        })
      }
    }

    // pozycja główna — nazwa jak w cenniku, np. "Usługi księgowe (do 50 dokumentów)"
    if (this.hasMainLineNameTarget) {
      let name = data.product_name || "-"
      if (matched) {
        const rangeStr = matched.from === 1
          ? `${this.tierUpToValue}${matched.to}`
          : `${matched.from}–${matched.to}`
        name = `${data.product_name} (${rangeStr} ${this.docsWordValue})`
      } else if (tiers.length > 0 && totalDocs > 0) {
        const lastTier = tiers[tiers.length - 1]
        name = `${data.product_name} (${this.tierUpToValue}${lastTier.to} ${this.docsWordValue})`
      }
      this.mainLineNameTarget.textContent = name
    }
    if (this.hasMainLineDescTarget) {
      this.mainLineDescTarget.textContent = `VAT ${data.vat_rate === "zw" ? this.vatExemptValue : data.vat_rate + "%"}`
    }
    if (this.hasMainLinePriceTarget) this.mainLinePriceTarget.textContent = this._formatCurrency(mainPrice)

    // overflow
    if (this.hasOverflowRowTarget) {
      this.overflowRowTarget.classList.toggle("hidden", overflowPrice <= 0)
      if (overflowPrice > 0) {
        if (this.hasOverflowNameTarget) this.overflowNameTarget.textContent = `${this.overflowDocsLabelValue} (${overflowDocs})`
        if (this.hasOverflowDescTarget) this.overflowDescTarget.textContent = `VAT ${data.vat_rate === "zw" ? this.vatExemptValue : data.vat_rate + "%"}`
        if (this.hasOverflowPriceTarget) this.overflowPriceTarget.textContent = this._formatCurrency(overflowPrice)
      }
    }

    // suma = główna + overflow + extras + jednorazowe
    let total = mainPrice + overflowPrice
    total += this._sumExtras()
    total += this._sumOnetime()

    if (this.hasGrandTotalTarget) this.grandTotalTarget.textContent = this._formatCurrency(total)
  }

  addOnetimeItem() {
    if (!this.hasOnetimeTemplateTarget || !this.hasOnetimeContainerTarget) return
    const clone = this.onetimeTemplateTarget.content.cloneNode(true)
    this.onetimeContainerTarget.appendChild(clone)
    this.recalculate()
  }

  removeOnetimeItem(event) {
    const row = event.currentTarget.closest(".onetime-row")
    if (row) row.remove()
    this.recalculate()
  }

  _sumExtras() {
    let sum = 0
    this.extraRowTargets.forEach(row => {
      const checkbox = row.querySelector("input[type=checkbox]")
      if (checkbox && checkbox.checked) {
        sum += parseFloat(checkbox.dataset.price) || 0
      }
    })
    return sum
  }

  _sumOnetime() {
    let sum = 0
    this.onetimeRowTargets.forEach(row => {
      const priceInput = row.querySelector("input[type=number]")
      if (priceInput) sum += parseFloat(priceInput.value) || 0
    })
    return sum
  }

  _formatCurrency(value) {
    return value.toFixed(2).replace(".", ",") + " " + this.currencySymbolValue
  }
}
